import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { apiJson } from '../../lib/api'
import { LoadSeries, Rating, SystemLoadResponse } from './api'
import { StatusPill } from './DashboardPage'
import base from './GodMode.module.css'
import styles from './SystemLoad.module.css'

// God Mode's System load: the last six hours of the API and the database from
// Cloud Monitoring (services/system-load.js), one small line chart each, with
// the limit it is rated by drawn dashed. Asks again every minute while the tab
// is open; when Monitoring cannot be read it says why instead.

const EVERY_MS = 60_000
const W = 320
const H = 96

const time = (t: string) => new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
const value = (v: number | null, unit: string) => (v === null ? '—' : `${v.toLocaleString('en-NZ')}${unit === '%' ? ' %' : unit === 'ms' ? ' ms' : unit === '/min' ? ' /min' : ''}`)

function normal(r: any): SystemLoadResponse | null {
  if (!r || typeof r !== 'object' || Array.isArray(r)) return null
  if (r.available !== true) return { available: false, reason: String(r.reason || 'System load is not available.') }
  return { ...r, series: Array.isArray(r.series) ? r.series : [] }
}

export function SystemLoad() {
  const { user } = useAuth()
  const [data, setData] = useState<SystemLoadResponse | null>(null)

  const load = useCallback(async () => {
    try {
      setData(normal(await apiJson<unknown>(user, '/api/god-mode/system-load')))
    } catch (e) {
      setData({ available: false, reason: (e as Error).message })
    }
  }, [user])

  useEffect(() => {
    load()
    const t = setInterval(() => {
      if (document.visibilityState !== 'hidden') load()
    }, EVERY_MS)
    return () => clearInterval(t)
  }, [load])

  if (!data) return null
  return (
    <section className={base.card} aria-label="System load">
      <h2>System load, last {data.available ? data.window_h : 6} hours</h2>
      {!data.available ? (
        <p className={styles.note}>{data.reason}</p>
      ) : (
        <>
          <div className={styles.grid}>
            {data.series.map((s) => (
              <Chart key={s.key} s={s} from={data.from} to={data.at} />
            ))}
          </div>
          <p className={styles.note}>
            {data.step_min}-minute steps from Cloud Monitoring. The dashed line is where a figure turns to Check; the database alerts mail support at the
            Problem limit.
          </p>
        </>
      )}
    </section>
  )
}

function Chart({ s, from, to }: { s: LoadSeries; from: string; to: string }) {
  const [hover, setHover] = useState<number | null>(null)
  const t0 = Date.parse(from)
  const span = Math.max(1, Date.parse(to) - t0)
  // The scale always reaches the Check limit, so a quiet line sits low rather
  // than being stretched to look busy.
  const top = Math.max((s.peak ?? 0) * 1.15, s.warn ?? 0, 1)
  const x = (t: string) => ((Date.parse(t) - t0) / span) * W
  const y = (v: number) => H - (v / top) * H
  const path = s.points.map((p, i) => `${i ? 'L' : 'M'}${x(p.t).toFixed(1)},${y(p.v).toFixed(1)}`).join(' ')
  const limits = [
    { at: s.warn, status: 'warn' as Rating },
    { at: s.bad, status: 'bad' as Rating },
  ].filter((l): l is { at: number; status: Rating } => l.at !== null && l.at <= top)
  const p = hover === null ? null : s.points[hover]

  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!s.points.length) return
    const r = e.currentTarget.getBoundingClientRect()
    const at = t0 + ((e.clientX - r.left) / r.width) * span
    let best = 0
    s.points.forEach((q, i) => {
      if (Math.abs(Date.parse(q.t) - at) < Math.abs(Date.parse(s.points[best].t) - at)) best = i
    })
    setHover(best)
  }

  return (
    <figure className={styles.chart} data-testid={`load-${s.key}`}>
      <figcaption className={styles.head}>
        <span className={styles.label}>{s.label}</span>
        <StatusPill status={s.status}>{value(s.now, s.unit)}</StatusPill>
      </figcaption>
      <div className={styles.plot}>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
          role="img"
          aria-label={`${s.label}: now ${value(s.now, s.unit)}, peak ${value(s.peak, s.unit)}`}
          onMouseMove={onMove}
          onMouseLeave={() => setHover(null)}
        >
          <line className={styles.base} x1={0} x2={W} y1={H} y2={H} vectorEffect="non-scaling-stroke" />
          {limits.map((l) => (
            <line key={l.status} className={styles[`limit_${l.status}`]} x1={0} x2={W} y1={y(l.at)} y2={y(l.at)} vectorEffect="non-scaling-stroke" />
          ))}
          {path && <path className={styles.line} d={path} vectorEffect="non-scaling-stroke" />}
          {p && <line className={styles.cross} x1={x(p.t)} x2={x(p.t)} y1={0} y2={H} vectorEffect="non-scaling-stroke" />}
        </svg>
        {limits.map((l) => (
          <span key={l.status} className={styles.limitLabel} style={{ top: `${(y(l.at) / H) * 100}%` }}>
            {l.status === 'bad' ? 'Problem' : 'Check'} {value(l.at, s.unit)}
          </span>
        ))}
        {p && (
          <span className={styles.dot} style={{ left: `${(x(p.t) / W) * 100}%`, top: `${(y(p.v) / H) * 100}%` }} aria-hidden="true" />
        )}
        {p && (
          <div className={styles.tip} style={{ left: `${Math.min(Math.max((x(p.t) / W) * 100, 15), 85)}%` }} role="status">
            <strong>{value(p.v, s.unit)}</strong> at {time(p.t)}
          </div>
        )}
        {!s.points.length && <span className={styles.empty}>No figures in this window</span>}
      </div>
      <div className={styles.axis}>
        <span>{time(from)}</span>
        <span>
          peak {value(s.peak, s.unit)}
          {s.peak_status === 'warn' || s.peak_status === 'bad' ? ` (${s.peak_status === 'bad' ? 'Problem' : 'Check'})` : ''}
        </span>
        <span>now</span>
      </div>
    </figure>
  )
}
